import { Box, Button, VStack, Text, Divider, Flex, Spacer, useColorModeValue } from '@chakra-ui/react';
import { useUser } from '../../../../../Context';
import { useState } from 'react';

export const MembershipForm = () => {
  const { user } = useUser();
  const [isPrintMode, setIsPrintMode] = useState(false);
  
  // All hooks must be called at the top level
  const errorBgColor = useColorModeValue('red.50', 'red.900');
  const errorTextColor = useColorModeValue('red.800', 'red.200');
  const errorSubTextColor = useColorModeValue('red.600', 'red.300');

  // Format user's full name from Cognito attributes
  const getUserFullName = () => {
    if (!user) {
      return 'Member';
    }

    // First try to get name from Cognito attributes
    if (user.attributes) {
      const firstName = user.attributes.given_name || user.attributes.name || '';
      const middleInitial = user.attributes.middle_name ? user.attributes.middle_name.charAt(0) + '.' : '';
      const lastName = user.attributes.family_name || '';

      // Construct full name with proper spacing
      let fullName = firstName;
      if (middleInitial) {
        fullName += ` ${middleInitial}`;
      }
      if (lastName) {
        fullName += ` ${lastName}`;
      }

      // If we got a valid name from attributes, return it
      if (fullName.trim()) {
        return fullName.trim();
      }
    }

    // Fallback: Try to parse username if it follows a pattern like "matthew_p_anthony"
    if (user.username) {
      const username = user.username;
      
      // Check if username contains underscores (likely name pattern)
      if (username.includes('_')) {
        const parts = username.split('_');
        if (parts.length >= 2) {
          // Capitalize first letter of each part
          const formattedParts = parts.map(part => 
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          );
          
          // If we have 3 parts, treat middle as initial
          if (formattedParts.length === 3) {
            return `${formattedParts[0]} ${formattedParts[1]} ${formattedParts[2]}`;
          } else {
            return formattedParts.join(' ');
          }
        }
      }
      
      // If no underscore pattern, just capitalize the username
      return username.charAt(0).toUpperCase() + username.slice(1).toLowerCase();
    }

    return 'Member';
  };
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const currentDateTime = new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });

  const handlePrint = () => {
    setIsPrintMode(true);
    setTimeout(() => {
      window.print();
      setIsPrintMode(false);
    }, 100);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden;
      }
      .printable-form, .printable-form * {
        visibility: visible;
      }
      .printable-form {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: white;
        padding: 40px;
        font-family: 'Times New Roman', serif;
        font-size: 12pt;
        line-height: 1.5;
      }
      .no-print {
        display: none !important;
      }
      .print-header {
        text-align: center;
        margin-bottom: 30px;
      }
      .print-title {
        font-size: 18pt;
        font-weight: bold;
        margin-bottom: 10px;
      }
      .print-subtitle {
        font-size: 14pt;
        margin-bottom: 20px;
      }
      .form-content {
        margin: 30px 0;
        line-height: 2;
      }
      .signature-section {
        margin-top: 50px;
        page-break-inside: avoid;
      }
      .signature-line {
        border-bottom: 1px solid black;
        width: 300px;
        display: inline-block;
        margin: 0 10px;
      }
    }
  `;

  if (!user) {
    return (
      <Box mt={6} p={6} bg={errorBgColor} borderRadius="md">
        <Text color={errorTextColor} fontWeight="bold">
          You must be logged in to access the membership form.
        </Text>
        <Text color={errorSubTextColor} mt={2}>
          Please log in to generate your official proof of membership document.
        </Text>
      </Box>
    );
  }

  return (
    <>
      <style>{printStyles}</style>
      <Box mt={6}>
        {/* Original content text */}
        <Text mb={6} color="black">
          This form serves as official documentation of your membership status at Golden Gun Club for CMP rifle purchase requirements.
        </Text>
        <Box className="no-print" mb={6}>
          <Text fontSize="lg" mb={4}>
            This form will be generated with your membership information and can be printed for CMP rifle purchase documentation.
          </Text>
          <Button colorScheme="blue" onClick={handlePrint} size="lg">
            Print Membership Form
          </Button>
        </Box>

        <Box 
          className="printable-form"
          bg="white" 
          p={8} 
          border="2px solid" 
          borderColor="gray.300" 
          borderRadius="md"
          maxW="8.5in"
          mx="auto"
        >
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <Box className="print-header" textAlign="center">
              <Text className="print-title" fontSize="2xl" fontWeight="bold">
                PROOF OF MEMBERSHIP
              </Text>
              <Text className="print-subtitle" fontSize="lg" color="gray.600">
                Golden Gun Club
              </Text>
              <Text fontSize="md" color="gray.500">
                1503 S Watkins Road, Watkins, CO 80137
              </Text>
              <Text fontSize="md" color="gray.500">
                Clubhouse: 303-366-6970 | pro-shop@goldengunclub.us
              </Text>
            </Box>

            <Divider borderColor="gray.400" />

            {/* Form Content */}
            <Box className="form-content">
              <Text fontSize="lg" mb={6}>
                <strong>TO WHOM IT MAY CONCERN:</strong>
              </Text>

              <Text fontSize="md" mb={4} lineHeight="tall">
                This letter serves as official documentation that{' '}
                <strong style={{ textDecoration: 'underline' }}>
                  {getUserFullName()}
                </strong>{' '}
                is a current member in good standing of the Golden Gun Club, located in Watkins, Colorado.
              </Text>

              <Text fontSize="md" mb={4} lineHeight="tall">
                Golden Gun Club is a CMP-affiliated organization dedicated to promoting marksmanship, 
                firearm safety, and the shooting sports. Our club has been serving the Denver Metro 
                area since 1947 and maintains facilities for both rifle/pistol and shotgun disciplines.
              </Text>

              <Text fontSize="md" mb={4} lineHeight="tall">
                This membership verification is issued for the purpose of meeting Civilian Marksmanship 
                Program (CMP) eligibility requirements for rifle purchases.
              </Text>

              <Text fontSize="md" mb={6} lineHeight="tall">
                <strong>Date of Verification:</strong> {currentDateTime}
              </Text>
            </Box>

            <Divider borderColor="gray.400" />

            {/* Signature Section */}
            <Box className="signature-section">
              <Text fontSize="md" fontWeight="bold" mb={4}>
                GOLDEN GUN CLUB:
              </Text>

              <Box>
                <Text fontSize="md" mb={2}>
                  <strong>David Thrower</strong>, President
                </Text>
                <Text fontSize="md" color="gray.600">
                  Date: {currentDate}
                </Text>
              </Box>

              <Box mt={8} p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="xs" color="gray.600" textAlign="center">
                  This document was generated electronically on {currentDateTime} and serves as 
                  official verification of membership status for CMP eligibility purposes.
                </Text>
              </Box>
            </Box>
          </VStack>
        </Box>
      </Box>
    </>
  );
};